'use client'

import React from 'react'
import { Header } from '@/payload-types'
import { CMSLink } from '@/components/Link'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { Link } from '@/i18n/routing'
import { cn } from '@/utilities/cn'
import { getCategoryHref } from '@/utilities/categoryHref'
import { useTranslations } from 'next-intl'
import { usePathname } from '@/i18n/routing'
import { useSearchParams } from 'next/navigation'

interface HeaderNavProps {
  header: Header
}

// Prevent hover from opening dropdown — click only
const preventHover = {
  onPointerEnter: (e: React.PointerEvent) => e.preventDefault(),
  onPointerMove: (e: React.PointerEvent) => e.preventDefault(),
  onPointerLeave: (e: React.PointerEvent) => e.preventDefault(),
}

export function HeaderNav({ header }: HeaderNavProps) {
  const navItems = header.navItems || []
  const t = useTranslations('common')
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Check if a nav item URL matches the current route
  const isActive = (url: string) => {
    if (url === '#') return false
    // For /shop?category=X URLs
    if (url.startsWith('/shop?category=')) {
      const catId = url.split('category=')[1]
      return pathname === '/shop' && searchParams.get('category') === catId
    }
    // For direct URLs
    return pathname === url
  }

  return (
    <NavigationMenu className="h-full">
      <NavigationMenuList className="h-full gap-1 lg:gap-2">
        {navItems.map((item) => {
          const hasSubNav = item.subNavItems && item.subNavItems.length > 0
          const itemUrl = getCategoryHref(item.categoryId, item.link.url)

          if (hasSubNav) {
            // Check if any child category is active
            const isParentActive =
              isActive(itemUrl) ||
              item.subNavItems?.some((sub) => {
                const subUrl = getCategoryHref(sub.categoryId, sub.link.url)
                if (isActive(subUrl)) return true
                return sub.subSubNavItems?.some((subsub) => {
                  const subsubUrl = getCategoryHref(subsub.categoryId, subsub.link.url)
                  return isActive(subsubUrl)
                })
              })

            return (
              <NavigationMenuItem key={item.id} className="h-full flex items-center">
                <NavigationMenuTrigger
                  className={cn(
                    'h-full font-mono uppercase tracking-wider text-sm !bg-transparent hover:!bg-transparent focus:!bg-transparent data-[state=open]:!bg-transparent',
                    isParentActive
                      ? 'text-[var(--neon-orange)] hover:text-[var(--neon-orange)] data-[state=open]:text-[var(--neon-orange)]'
                      : 'text-white hover:text-[var(--neon-orange)] data-[state=open]:text-[var(--neon-orange)]',
                  )}
                  {...preventHover}
                >
                  {item.link.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent
                  onPointerEnter={(e) => e.preventDefault()}
                  onPointerLeave={(e) => e.preventDefault()}
                >
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[600px] md:grid-cols-3 lg:w-[800px] max-h-[calc(100vh-6rem)] overflow-y-auto">
                    {/* "View All" link for the top-level category */}
                    <li className="col-span-full border-b border-white/10 pb-3 mb-1">
                      <NavigationMenuLink asChild>
                        <Link
                          href={itemUrl !== '#' ? itemUrl : '/shop'}
                          className="text-sm font-mono font-semibold uppercase tracking-wider text-[var(--neon-orange)] hover:underline outline-none transition-colors"
                        >
                          {t('viewAll')} — {item.link.label}
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    {(!item.subNavItems || item.subNavItems.length === 0) && (
                      <li className="col-span-full text-sm text-muted-foreground font-mono py-2">
                        Keine Unterkategorien vorhanden
                      </li>
                    )}
                    {item.subNavItems?.map((subItem) => {
                      const subItemUrl = getCategoryHref(subItem.categoryId, subItem.link.url)

                      const hasSubSubNav =
                        subItem.subSubNavItems && subItem.subSubNavItems.length > 0

                      return (
                        <li key={subItem.id} className="flex flex-col gap-2 p-2">
                          <NavigationMenuLink asChild>
                            <Link
                              href={subItemUrl}
                              className="text-base font-mono font-medium leading-none hover:text-[var(--neon-orange)] hover:underline outline-none uppercase tracking-tight transition-colors"
                            >
                              {subItem.link.label}
                            </Link>
                          </NavigationMenuLink>

                          {hasSubSubNav && (
                            <ul className="flex flex-col gap-2">
                              {/* "View All" for this sub-category */}
                              <li>
                                <NavigationMenuLink asChild>
                                  <Link
                                    href={subItemUrl !== '#' ? subItemUrl : '/shop'}
                                    className="text-xs font-mono font-semibold uppercase tracking-wider text-[var(--neon-orange)] hover:underline outline-none transition-colors"
                                  >
                                    {t('viewAll')}
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                              {subItem.subSubNavItems?.map((subSubItem) => {
                                const subSubItemUrl = getCategoryHref(
                                  subSubItem.categoryId,
                                  subSubItem.link.url,
                                )

                                return (
                                  <li key={subSubItem.id}>
                                    <NavigationMenuLink asChild>
                                      <Link
                                        href={subSubItemUrl}
                                        className="text-sm font-sans text-muted-foreground hover:text-[var(--neon-orange)] hover:underline line-clamp-1 outline-none transition-colors"
                                      >
                                        {subSubItem.link.label}
                                      </Link>
                                    </NavigationMenuLink>
                                  </li>
                                )
                              })}
                            </ul>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            )
          }

          return (
            <NavigationMenuItem key={item.id} className="h-full flex items-center">
              <NavigationMenuLink asChild>
                <Link
                  href={itemUrl}
                  className={cn(
                    navigationMenuTriggerStyle(),
                    'h-full font-mono uppercase tracking-wider text-sm !bg-transparent hover:!bg-transparent focus:!bg-transparent',
                    isActive(itemUrl)
                      ? 'text-[var(--neon-orange)] hover:text-[var(--neon-orange)] focus:text-[var(--neon-orange)]'
                      : 'text-white hover:text-[var(--neon-orange)] focus:text-[var(--neon-orange)]',
                  )}
                >
                  {item.link.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>
          )
        })}
      </NavigationMenuList>
    </NavigationMenu>
  )
}
